/**
 * Shared voting-state source of truth.
 *
 * The canonical launch instant lives in the database (`app_settings` row with
 * key `voting_settings`, value.launch_at as an ISO UTC instant). Nothing here
 * hardcodes a launch date — every value comes from the stored configuration.
 */

export const VOTING_SETTINGS_KEY = "voting_settings";

export const MANUAL_OVERRIDES = ["open", "closed"] as const;
export type ManualOverride = (typeof MANUAL_OVERRIDES)[number];

export type VotingState = "scheduled" | "open" | "closed";

export type PublicVotingState = {
  configured: boolean;
  /** True only when launch AND close exist and close is strictly after launch. */
  scheduleComplete: boolean;
  state: VotingState;
  /** ISO UTC instant, or null when not configured. */
  launchAt: string | null;
  /** ISO UTC instant of the scheduled close, or null when not configured. */
  closeAt: string | null;
  /** IANA identifier, or null when not configured. */
  displayTimezone: string | null;
  /** ISO UTC instant of the server clock at response time. */
  serverNow: string;
  /** Active voting data context: "PROD" or TEST-01/02/03. Server-derived. */
  dataContext: string;
  testMode: boolean;
};


/** Common US options surfaced first in the admin picker. */
export const COMMON_TIMEZONES: { value: string; label: string }[] = [
  { value: "America/New_York", label: "Eastern — America/New_York" },
  { value: "America/Chicago", label: "Central — America/Chicago" },
  { value: "America/Denver", label: "Mountain — America/Denver" },
  { value: "America/Los_Angeles", label: "Pacific — America/Los_Angeles" },
  { value: "America/Phoenix", label: "Arizona — America/Phoenix" },
  { value: "America/Anchorage", label: "Alaska — America/Anchorage" },
  { value: "Pacific/Honolulu", label: "Hawaii — Pacific/Honolulu" },
  { value: "UTC", label: "UTC" },
];

export function isValidTimeZone(tz: string): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(0);
    return true;
  } catch {
    return false;
  }
}

/**
 * True only when both endpoints exist, parse, and close is strictly after
 * launch. An incomplete schedule is never treated as "open forever".
 */
export function isScheduleComplete(input: {
  launchAt: string | null;
  closeAt: string | null;
}): boolean {
  if (!input.launchAt || !input.closeAt) return false;
  const launch = Date.parse(input.launchAt);
  const close = Date.parse(input.closeAt);
  if (Number.isNaN(launch) || Number.isNaN(close)) return false;
  return close > launch;
}

/**
 * Effective-state precedence (single implementation, mirrored by the
 * `public.get_voting_state()` SQL function which is authoritative):
 *   1. override "open"            -> open
 *   2. override "closed"          -> closed
 *   3. incomplete schedule        -> closed (fail closed)
 *   4. now < launch_at            -> scheduled
 *   5. launch_at <= now < close_at-> open
 *   6. now >= close_at            -> closed
 */
export function deriveVotingState(input: {
  launchAt: string | null;
  closeAt: string | null;
  manualOverride: ManualOverride | null;
  now: Date | number;
}): VotingState {
  if (input.manualOverride === "open") return "open";
  if (input.manualOverride === "closed") return "closed";
  if (!isScheduleComplete(input)) return "closed";
  const launch = Date.parse(input.launchAt!);
  const close = Date.parse(input.closeAt!);
  const now = typeof input.now === "number" ? input.now : input.now.getTime();
  if (now < launch) return "scheduled";
  return now < close ? "open" : "closed";
}


/** Offset in ms of `tz` at the given instant (localTime - utcTime). */
function tzOffsetMs(utcMs: number, tz: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = Object.fromEntries(
    dtf.formatToParts(new Date(utcMs)).map((p) => [p.type, p.value]),
  ) as Record<string, string>;
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) === 24 ? 0 : Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - utcMs;
}

export type ZonedConversion =
  | { ok: true; utcISO: string }
  | { ok: false; reason: "invalid-input" | "invalid-timezone" | "dst-gap" | "dst-ambiguous" };

/**
 * Convert a wall-clock date + time in an IANA zone to one unambiguous UTC
 * instant. DST gaps and ambiguous repeated hours are reported, never silently
 * adjusted.
 */
export function zonedDateTimeToUtc(
  dateStr: string,
  timeStr: string,
  tz: string,
): ZonedConversion {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr) || !/^\d{2}:\d{2}$/.test(timeStr)) {
    return { ok: false, reason: "invalid-input" };
  }
  if (!isValidTimeZone(tz)) return { ok: false, reason: "invalid-timezone" };

  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  if (hh > 23 || mm > 59) return { ok: false, reason: "invalid-input" };
  const naive = Date.UTC(y, m - 1, d, hh, mm, 0);

  const candidates = new Set<number>();
  const o1 = tzOffsetMs(naive, tz);
  candidates.add(naive - o1);
  const o2 = tzOffsetMs(naive - o1, tz);
  candidates.add(naive - o2);

  const valid = [...candidates].filter((ms) => tzOffsetMs(ms, tz) + ms === naive);
  if (valid.length === 0) return { ok: false, reason: "dst-gap" };
  if (valid.length > 1) return { ok: false, reason: "dst-ambiguous" };
  return { ok: true, utcISO: new Date(valid[0]).toISOString() };
}

/** Split a UTC instant back into wall-clock date/time strings for a zone. */
export function utcToZonedFields(
  utcISO: string,
  tz: string,
): { date: string; time: string } | null {
  const ms = Date.parse(utcISO);
  if (Number.isNaN(ms) || !isValidTimeZone(tz)) return null;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
      .formatToParts(new Date(ms))
      .map((p) => [p.type, p.value]),
  ) as Record<string, string>;
  const hour = parts.hour === "24" ? "00" : parts.hour;
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${hour}:${parts.minute}`,
  };
}

export function formatInZone(utcISO: string, tz: string): string {
  const ms = Date.parse(utcISO);
  if (Number.isNaN(ms)) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: isValidTimeZone(tz) ? tz : "UTC",
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(ms));
}

/* ------------------------------------------------------------------ */
/* Daily vote reset (one vote per nominee per daily voting period)      */
/* ------------------------------------------------------------------ */

export const DAILY_RESET_SETTINGS_KEY = "voting_daily_reset";

export type DailyVoteReset = {
  /** 24h wall-clock "HH:MM" in `timezone`. */
  resetTime: string;
  /** IANA identifier. */
  timezone: string;
};

export const DEFAULT_DAILY_VOTE_RESET: DailyVoteReset = {
  resetTime: "00:00",
  timezone: "America/New_York",
};

export function isValidResetTime(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/**
 * Start of the daily voting period containing `at`, mirroring the
 * authoritative `public.current_vote_period_start()`.
 *
 * This is a FIXED wall-clock reset in the configured zone — never a rolling
 * 24-hour window from the voter's last vote.
 */
export function votePeriodStart(
  at: Date | number,
  reset: DailyVoteReset = DEFAULT_DAILY_VOTE_RESET,
): Date {
  const ms = typeof at === "number" ? at : at.getTime();
  const zone = isValidTimeZone(reset.timezone)
    ? reset.timezone
    : DEFAULT_DAILY_VOTE_RESET.timezone;
  const time = isValidResetTime(reset.resetTime)
    ? reset.resetTime
    : DEFAULT_DAILY_VOTE_RESET.resetTime;
  const [rh, rm] = time.split(":").map(Number);

  const off = tzOffsetMs(ms, zone);
  const local = new Date(ms + off); // UTC fields carry the local wall clock
  let startLocal = Date.UTC(
    local.getUTCFullYear(),
    local.getUTCMonth(),
    local.getUTCDate(),
    rh,
    rm,
    0,
  );
  if (local.getTime() < startLocal) startLocal -= 86_400_000;

  let utc = startLocal - off;
  const off2 = tzOffsetMs(utc, zone);
  if (off2 !== off) utc = startLocal - off2;
  return new Date(utc);
}

/** True when both votes fall inside the same daily voting period. */
export function inSameVotePeriod(
  a: Date | number,
  b: Date | number,
  reset: DailyVoteReset = DEFAULT_DAILY_VOTE_RESET,
): boolean {
  return votePeriodStart(a, reset).getTime() === votePeriodStart(b, reset).getTime();
}

export type Countdown = { days: number; hours: number; minutes: number; seconds: number };


/** Never negative. */
export function countdownParts(targetMs: number, nowMs: number): Countdown {
  let diff = Math.max(0, Math.floor((targetMs - nowMs) / 1000));
  const days = Math.floor(diff / 86400);
  diff -= days * 86400;
  const hours = Math.floor(diff / 3600);
  diff -= hours * 3600;
  const minutes = Math.floor(diff / 60);
  const seconds = diff - minutes * 60;
  return { days, hours, minutes, seconds };
}

/* ------------------------------------------------------------------ */
/* Display helpers (no voting logic)                                    */
/* ------------------------------------------------------------------ */

/**
 * Start of the NEXT daily voting period after `at`. Display-only: used to tell
 * a duplicate voter when they may vote for that nominee again.
 */
export function nextVotePeriodStart(
  at: Date | number,
  reset: DailyVoteReset = DEFAULT_DAILY_VOTE_RESET,
): Date {
  const start = votePeriodStart(at, reset);
  // Step forward ~25h then snap back to the period boundary so DST shifts
  // resolve to the correct wall-clock reset.
  return votePeriodStart(start.getTime() + 25 * 3_600_000, reset);
}

/** The viewer's detected IANA timezone, or null when not reliably detectable. */
export function detectedTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && isValidTimeZone(tz) ? tz : null;
  } catch {
    return null;
  }
}

/**
 * Human label for the next reset, converted to the viewer's local zone when
 * reliably possible. Falls back to the configured contest zone, which stays
 * authoritative either way.
 */
export function formatNextResetForViewer(
  next: Date,
  reset: DailyVoteReset = DEFAULT_DAILY_VOTE_RESET,
): string {
  const local = detectedTimeZone();
  const zone = local ?? (isValidTimeZone(reset.timezone) ? reset.timezone : "UTC");
  const label = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(next);
  return label;
}
